import { useState } from "react";
import { Button, DarkSelect } from "./ui";

interface TopProps {
  total: number;
  pageSize: number;
  onPageSizeChange: (n: number) => void;
  pageSizeOptions?: number[];
}

interface BottomProps {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (p: number) => void;
}

const DEFAULT_SIZES = [10, 25, 50, 100];

export function PaginationTop({
  total,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_SIZES,
}: TopProps) {
  return (
    <div className="flex items-center gap-3 text-sm text-zinc-400">
      <span className="text-zinc-500">всего: {total}</span>
      <div className="flex items-center gap-1.5">
        <span className="text-xs">на странице</span>
        <div className="w-20">
          <DarkSelect
            value={String(pageSize)}
            onChange={(v) => onPageSizeChange(Number(v))}
            options={pageSizeOptions.map((n) => ({ value: String(n), label: String(n) }))}
          />
        </div>
      </div>
    </div>
  );
}

export function PaginationBottom({ total, page, pageSize, onPageChange }: BottomProps) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const go = (p: number) => onPageChange(Math.min(pages, Math.max(1, p)));
  const commitDraft = () => {
    const n = parseInt(draft, 10);
    if (!Number.isNaN(n)) go(n);
    setEditing(false);
    setDraft("");
  };

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex items-center justify-between gap-4 mt-4 text-sm">
      <div className="text-zinc-500 tabular-nums">
        {from}–{to} из {total}
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" onClick={() => go(page - 1)} disabled={page === 1}>
          ◀
        </Button>
        {renderPageNumbers(page, pages).map((p, i) =>
          p === "…" ? (
            <span key={`e${i}`} className="px-2 text-zinc-600">…</span>
          ) : p === page && editing ? (
            <input
              key={`in${i}`}
              autoFocus
              className="h-8 w-14 px-2 rounded-lg bg-zinc-950 border border-brand text-center text-zinc-100"
              value={draft}
              onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ""))}
              onBlur={commitDraft}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitDraft();
                if (e.key === "Escape") {
                  setEditing(false);
                  setDraft("");
                }
              }}
            />
          ) : (
            <button
              key={`p${p}`}
              onClick={() => {
                if (p === page) {
                  setEditing(true);
                  setDraft(String(p));
                } else {
                  go(p as number);
                }
              }}
              className={`h-8 min-w-[2rem] px-2 rounded-lg text-sm ${
                p === page ? "bg-brand text-brand-fg" : "text-zinc-300 hover:bg-zinc-800"
              }`}
            >
              {p}
            </button>
          ),
        )}
        <Button variant="ghost" onClick={() => go(page + 1)} disabled={page >= pages}>
          ▶
        </Button>
      </div>
    </div>
  );
}

function renderPageNumbers(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | "…")[] = [1];
  if (current > 3) out.push("…");
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    out.push(p);
  }
  if (current < total - 2) out.push("…");
  out.push(total);
  return out;
}

export default PaginationBottom;
